// 我的工位 (personal desks): the office's word on who owns which desk (src/office/desks.ts: {t:"desks", list}, and
// `desks` in the welcome), what the desk panel (public/pick.js) offers for it, and what the map draws (deck/seats-view.js:
// a name plate over each owned desk and its owner's decorations on it; the owner's name tag gets a desk icon, app.js).
// - an empty desk: 申请为我的工位 (one desk per person per floor: with one already, the office refuses; release it first);
// - my desk: 释放工位, and 装饰: up to DESK_DECOR_MAX of DESK_DECOR (small things of the map's own art: the loose
//   sprites of the fronts image, MAP.fronts.sprites), toggled one by one;
// - someone else's: whose it is; an office admin (the server's owners and admins: desk_admin from the office; not
//   every member, though every member may arrange the map) also gets 释放（管理员）.
// Coming into the office with a desk here: a toast offers 回到我的工位 (walks there and sits, public/sit.js goSit).
// Desks are per floor: each floor is an office of its own.
import { seatById, sittingOn, seatDist, SEAT_REACH, DESK_DECOR, DESK_DECOR_MAX } from '/seats.js';
import { t, errorText } from '/i18n.js';

const DECOR_NAME = Object.fromEntries(DESK_DECOR);
let sheet = null; // the fronts image, for the toggles' pictures (loaded once)
/** A decoration's picture for its toggle: its sprite of the fronts image, scaled up whole-pixel to fit 20 px. */
function thumb(MAP, id) {
  const c = document.createElement('canvas'), r = MAP.fronts?.sprites?.[id];
  c.width = 20; c.height = 20; c.style.cssText = 'width:20px;height:20px;image-rendering:pixelated';
  if (!r) return c;
  const [sx, sy, w, h] = r, fit = Math.min(20 / w, 20 / h), k = fit >= 1 ? Math.floor(fit) : fit; // whole pixels when it fits
  const draw = () => { const g = c.getContext('2d'); g.imageSmoothingEnabled = false; g.drawImage(sheet, sx, sy, w, h, Math.round((20 - w * k) / 2), Math.round((20 - h * k) / 2), Math.round(w * k), Math.round(h * k)); };
  if (!sheet) { sheet = new Image(); sheet.src = MAP.fronts.image; }
  if (sheet.complete && sheet.naturalWidth) draw(); else sheet.addEventListener('load', draw, { once: true });
  return c;
}

/**
 * @param {object} o
 * @param {object} o.MAP  @param {() => object|null} o.me  @param {(m: object) => void} o.send
 * @param {object} o.sit public/sit.js (goSit)  @param {(text: string, go?: Function, icon?: string, goText?: string) => void} o.toast
 * @param {object} [o.mover] deck/move.js (keys, trips): walking off elsewhere calls off 回到我的工位
 * @param {() => boolean} [o.ready] the map is up and my otter drawn (the offer waits for it, not under the loading screen)
 */
export function initDesks({ MAP, me, send, sit, toast, mover = null, ready = () => true }) {
  let list = []; // [{seat, owner: {name, gid?}, decor: [...], mine?}]: the office's, with my own decorations change on top
  let sent = []; // the office's last list
  // My last decorations change ({seat, decor, until}), shown at once and kept over the office's lists until one has it:
  // a list sent for an earlier click (two clicks in a row) must not undo a later one. A refusal (desk_err) or 5 s drop it.
  let pend = null;
  const same = (a, b) => (a ?? []).join() === (b ?? []).join();
  function relist() {
    if (pend && (Date.now() > pend.until || same(sent.find((d) => d.seat === pend.seat)?.decor, pend.decor))) pend = null;
    list = sent.map((d) => (pend && d.seat === pend.seat ? { ...d, decor: pend.decor } : d));
  }
  let admin = false, offered = false;
  const myGid = () => me()?.gid ?? null;
  const byGid = (gid) => (gid ? list.find((d) => d.owner?.gid === gid) ?? null : null);
  /** Is this desk mine: the office says so (mine), or (an office from before that) its owner's login is mine. The office
   *  sends an owner's login only while they're here (src/office/desks.ts listFor). */
  const isMineD = (d) => !!d && (d.mine === true || (!!myGid() && d.owner?.gid === myGid()));
  /** The desk with this seat id's entry, or null (nobody owns it). */
  const of = (seat) => list.find((d) => d.seat === seat) ?? null;
  /** My own desk's entry (and its seat), or null. */
  function mine() { const d = list.find(isMineD); const s = d && seatById(MAP, d.seat); return s ? { ...d, s } : null; }
  // 回到我的工位: walk there and sit (sit.goSit). On a slow device the walk can outlast sit.js's own wait, so until it
  // sits (or 3 minutes pass, or I walk off again once there) sit down when I'm standing still beside it. Walking
  // somewhere else (a key, or a new trip: a double click elsewhere, 走过去...) calls it off.
  let going = null;
  const trips = () => mover?.trips?.() ?? 0; // deck/move.js: a new trip is one I didn't ask for here
  function goMine() {
    const d = mine(); if (!d) return;
    if (!sit.goSit(d.s)) { going = null; return toast(t('desks.taken')); }
    going = { seat: d.seat, until: Date.now() + 180000, near: false, tried: 0, trip: trips() };
  }
  const walkedOff = () => !!mover?.keys?.size || trips() !== going.trip;
  setInterval(() => {
    const m = me(), d = going && mine();
    if (!going) return;
    if (!m || !d || d.seat !== going.seat || Date.now() > going.until || sittingOn(MAP, m)?.id === d.seat || walkedOff()) { going = null; return; }
    const far = seatDist(d.s, m.x, m.y);
    if (far <= SEAT_REACH) going.near = true; else if (going.near) { going = null; return; }
    if (going.near && !m.moving && Date.now() - going.tried > 2000) {
      going.tried = Date.now();
      if (!sit.goSit(d.s)) { going = null; toast(t('desks.taken')); } else going.trip = trips();
    }
  }, 500);

  function msg(m) {
    if (m.t === 'desks') { sent = Array.isArray(m.list) ? m.list : []; relist(); }
    else if (m.t === 'desk_err') { if (pend?.seat === m.seat) { pend = null; relist(); } toast(m.reason || m.code ? errorText('desks.err', { code: m.code, message: m.reason, params: m.params }) : t('desks.failed')); } // desks.err.<code>
    else if (m.t === 'desk_admin') admin = m.admin === true;
  }
  /** The welcome: the desks as they are; the first time in, offer to go back to mine. */
  function welcome(desks) {
    sent = Array.isArray(desks) ? desks : []; relist();
    if (offered) return;
    offered = true;
    if (!mine()) return;
    const until = Date.now() + 600000, iv = setInterval(() => {
      if (Date.now() > until) return clearInterval(iv);
      if (!ready()) return;
      clearInterval(iv);
      // a moment after the map shows (the coach marks of a first visit come up first)
      setTimeout(() => { const d = mine(); if (d && sittingOn(MAP, me())?.id !== d.seat) toast(t('desks.offer'), goMine, 'desk', t('desks.goBack')); }, 1200);
    }, 300);
  }

  /** What the desk panel adds for desk s (public/pick.js seatTarget): a title, a line, actions and toggles. */
  function panel(s) {
    const d = of(s.id), m = me(), human = !!m && m.kind !== 'agent' && !m.guest && !m.visitor, isMine = !!m && isMineD(d);
    if (isMine) {
      const on = new Set(d.decor ?? []);
      const toggle = (id) => {
        // from the desk as it is now (an earlier toggle may have changed it since this panel was built)
        const cur = of(s.id)?.decor ?? [];
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        if (next.length > DESK_DECOR_MAX) return toast(t('desks.decor.full', { max: DESK_DECOR_MAX }));
        pend = { seat: s.id, decor: next, until: Date.now() + 5000 }; relist(); setTimeout(relist, 5100); // at once, so the next toggle builds on it
        send({ t: 'desk_decor', seat: s.id, decor: next });
      };
      return {
        title: t('desks.mine'), note: t('desks.decor.count', { n: on.size, max: DESK_DECOR_MAX }),
        actions: [{ icon: 'close', text: t('desks.release'), alt: true, go: () => { if (confirm(t('desks.release.confirm'))) send({ t: 'desk_release', seat: s.id }); } }],
        toggles: DESK_DECOR.map(([id, name]) => ({ icon: id, el: () => thumb(MAP, id), title: t(name), on: on.has(id), go: () => toggle(id) })),
      };
    }
    if (d) {
      const actions = admin && human ? [{ icon: 'close', text: t('desks.release.admin'), alt: true, go: () => { if (confirm(t('desks.release.adminConfirm', { name: d.owner.name }))) send({ t: 'desk_release', seat: s.id }); } }] : [];
      return { title: t('desks.owned', { name: d.owner.name }), note: null, actions, toggles: null };
    }
    if (!human) return { title: null, note: null, actions: [], toggles: null };
    if (mine()) return { title: null, note: t('desks.haveOne'), actions: [], toggles: null };
    return { title: null, note: null, actions: [{ icon: 'check', text: t('desks.claim'), go: () => send({ t: 'desk_claim', seat: s.id }) }], toggles: null };
  }

  return {
    msg, welcome, panel, mine, goMine, of,
    /** Does p own a desk here (their name tag's desk icon, app.js tagIcons)? */
    owns: (p) => !!p && !!byGid(p.gid) && !!seatById(MAP, byGid(p.gid).seat),
    /** For the map (deck/seats-view.js): every owned desk on the map: {s (the seat), name, mine, decor}. */
    drawn() {
      const out = [];
      for (const d of list) { const s = seatById(MAP, d.seat); if (s?.kind === 'desk') out.push({ s, name: d.owner?.name ?? '', mine: isMineD(d), decor: (d.decor ?? []).filter((x) => x in DECOR_NAME) }); }
      return out;
    },
  };
}
